const sectionNamePrefix = '__product_section_for_';
const sectionTitlePrefix = '__product_section_title_for_';
const firstProductSectionTitle = document.querySelector('.product-section-title');
const defaultCursor = firstProductSectionTitle ? firstProductSectionTitle.textContent.trim() : '';
const transparentPlaceholderSrc = 'data:image/gif;base64,R0lGODlhAQABAAAAACwAAAAAAQABAAA=';
const desktopMenuImageMaxWidth = 640;
const mobileMenuImageMaxWidth = 800;
const defaultMenuImageWarmupLimit = 2;
const deferredMenuRequestTimeout = 6000;
const deferredMenuRequestRetryDelay = 240;
const deferredMenuRequestMaxAttempts = 2;
let titleCursor = defaultCursor;
let defaultMenuPrepared = false;
let defaultMenuWarmupStarted = false;
let desktopMenuImageWarmupStarted = false;
let mobileMenuImageWarmupStarted = false;
let productSectionSwitchToken = 0;
const pendingRevealRetries = new WeakMap();
const pendingDeferredPanelLoads = new WeakMap();
const deferredMenuRequests = new Map();
const productSectionHideTimers = new WeakMap();
const mobileMenuPanelWarmupTargets = new WeakSet();
const mobileMenuLinkBindings = new WeakSet();
const mobileProductDetailsBindings = new WeakSet();
const mobileMenuAnchorTopGap = 12;

function clearRevealRetry(container) {
	const retryTimer = pendingRevealRetries.get(container);

	if (retryTimer) {
		window.clearTimeout(retryTimer);
		pendingRevealRetries.delete(container);
	}
}

function clearProductSectionHideTimer(target) {
	const timer = productSectionHideTimers.get(target);

	if (timer) {
		window.clearTimeout(timer);
		productSectionHideTimers.delete(target);
	}
}

function queueRevealRetry(container, attempt) {
	if (!container || pendingRevealRetries.has(container) || attempt >= 6) {
		return;
	}

	const retryDelay = attempt < 2 ? 48 : 96;
	const retryTimer = window.setTimeout(() => {
		pendingRevealRetries.delete(container);
		revealLazyImages(container, attempt + 1);
	}, retryDelay);

	pendingRevealRetries.set(container, retryTimer);
}

function hasLoadedMenuImageSource(image) {
	if (!image) {
		return false;
	}

	const currentSrc = image.currentSrc || image.getAttribute('src') || '';
	return !!currentSrc && currentSrc !== transparentPlaceholderSrc;
}

function withPreviewThemeParam(url) {
	try {
		const parsedUrl = new URL(url, window.location.origin);
		const previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');

		if (previewThemeId && !parsedUrl.searchParams.has('preview_theme_id')) {
			parsedUrl.searchParams.set('preview_theme_id', previewThemeId);
		}

		return parsedUrl.toString();
	} catch (error) {
		return url;
	}
}

function getDeferredPanelVariant(target, documentFragment, variant) {
	const isFirstPanel = target.dataset.menuPanelFirst === 'true';
	const variantName = isFirstPanel ? 'first' : 'default';
	return documentFragment.querySelector(`[data-menu-panel-${variant}-${variantName}]`);
}

function bindProductItemLinks(container) {
	if (!container) return;

	container.querySelectorAll('.product-section-item[data-link], .product-section-item-mobile[data-link]').forEach(el => {
		if (el.dataset.menuLinkBound === 'true') return;

		el.dataset.menuLinkBound = 'true';
		el.addEventListener('click', e => {
			if (e.ctrlKey) {
				e.preventDefault();
				const targetUrl = el.getAttribute('data-link');
				const newWindow = window.open(targetUrl, '_blank');
				newWindow.location.href = targetUrl;
			} else {
				location.href = el.getAttribute('data-link');
			}
		});
	});
}

function fetchDeferredMenuDocument(requestUrl, attempt = 0) {
	const controller = typeof AbortController === 'function' ? new AbortController() : null;
	const requestOptions = { credentials: 'same-origin' };
	let timeoutId = null;

	if (controller) {
		requestOptions.signal = controller.signal;
		timeoutId = window.setTimeout(() => controller.abort(), deferredMenuRequestTimeout);
	}

	return fetch(requestUrl, requestOptions)
		.then(response => {
			if (!response.ok) {
				throw new Error(`Deferred menu request failed: ${response.status}`);
			}
			return response.text();
		})
		.then(html => new DOMParser().parseFromString(html, 'text/html'))
		.catch(error => {
			if (attempt + 1 >= deferredMenuRequestMaxAttempts) {
				throw error;
			}

			return new Promise(resolve => {
				window.setTimeout(resolve, deferredMenuRequestRetryDelay);
			}).then(() => fetchDeferredMenuDocument(requestUrl, attempt + 1));
		})
		.finally(() => {
			if (timeoutId) {
				window.clearTimeout(timeoutId);
			}
		});
}

function loadDeferredProductPanel(target, variant, options = {}) {
	if (!target || !target.dataset.menuPanelUrl) {
		return Promise.resolve(false);
	}

	if (target.dataset.menuPanelLoaded === variant) {
		return Promise.resolve(true);
	}
	if (pendingDeferredPanelLoads.has(target)) {
		return pendingDeferredPanelLoads.get(target);
	}

	const requestUrl = withPreviewThemeParam(target.dataset.menuPanelUrl);
	target.dataset.menuPanelState = 'loading';
	target.setAttribute('aria-busy', 'true');

	if (!deferredMenuRequests.has(requestUrl)) {
		deferredMenuRequests.set(requestUrl, fetchDeferredMenuDocument(requestUrl));
	}

	const panelLoad = deferredMenuRequests
		.get(requestUrl)
		.then(documentFragment => {
			const source = getDeferredPanelVariant(target, documentFragment, variant);
			if (!source) {
				throw new Error(`Deferred menu variant missing: ${variant}`);
			}

			target.innerHTML = source.innerHTML;
			target.dataset.menuPanelLoaded = variant;
			target.dataset.menuPanelState = 'loaded';
			target.removeAttribute('aria-busy');
			bindProductItemLinks(target);
			if (options.reveal !== false) {
				revealLazyImages(target);
			}
			return true;
		})
		.catch(error => {
			deferredMenuRequests.delete(requestUrl);
			target.dataset.menuPanelState = 'error';
			target.removeAttribute('aria-busy');
			console.warn(error);
			return false;
		});
	const settledPanelLoad = panelLoad.finally(() => {
		pendingDeferredPanelLoads.delete(target);
	});
	pendingDeferredPanelLoads.set(target, settledPanelLoad);
	return settledPanelLoad;
}

function settleMenuImage(image) {
	image.dataset.menuImageRevealed = 'true';
	delete image.dataset.menuImageRevealing;
	image.removeAttribute('data-src');
	image.removeAttribute('data-srcset');
	image.removeAttribute('data-sizes');
	image.classList.remove('header-navigation-image--pending', 'lazyload', 'lazyloading');
	image.classList.add('lazyloaded');
}

function getMenuImageMaxWidth(image) {
	if (image && image.closest('.mobile-menu, .full-menu-mobile-details, .product-section-item-mobile')) {
		return mobileMenuImageMaxWidth;
	}

	return desktopMenuImageMaxWidth;
}

function getMenuImageFallbackWidth(image) {
	if (image && image.closest('.mobile-menu, .full-menu-mobile-details, .product-section-item-mobile')) {
		return 280;
	}

	return 180;
}

function getWidthFromSrcset(srcset) {
	if (!srcset) return 0;

	const widthMatches = srcset.match(/\s(\d+)w/g);
	if (!widthMatches) return 0;

	return widthMatches.reduce((maxWidth, match) => {
		const width = Number(match.replace('w', '').trim());
		return Number.isFinite(width) ? Math.max(maxWidth, width) : maxWidth;
	}, 0);
}

function getMenuImageTargetWidth(image) {
	const rect = image.getBoundingClientRect();
	const cssWidth = rect.width > 0 ? rect.width : getMenuImageFallbackWidth(image);
	const maxWidth = getMenuImageMaxWidth(image);
	const requiredWidth = Math.ceil(cssWidth * Math.min(window.devicePixelRatio || 1, 3));
	const buckets = [240, 320, 500, 640, 800];
	const bucket = buckets.find(width => width >= requiredWidth) || maxWidth;

	return Math.min(bucket, maxWidth);
}

function getResizedShopifyImageUrl(url, width) {
	if (!url || url === transparentPlaceholderSrc || !width) return url;

	try {
		const parsedUrl = new URL(url, window.location.origin);
		parsedUrl.searchParams.set('width', String(width));
		return parsedUrl.toString();
	} catch (error) {
		return url;
	}
}

function ensureMenuImageResolution(image) {
	const targetWidth = getMenuImageTargetWidth(image);
	const currentSrcset = image.dataset.srcset || image.getAttribute('srcset') || '';
	const maxSrcsetWidth = getWidthFromSrcset(currentSrcset);

	if (maxSrcsetWidth >= targetWidth) return;

	const baseUrl = image.dataset.src || image.getAttribute('src') || image.currentSrc;
	const resizedUrl = getResizedShopifyImageUrl(baseUrl, targetWidth);
	if (!resizedUrl || resizedUrl === baseUrl && maxSrcsetWidth) return;

	const nextSrcset = currentSrcset ? `${currentSrcset}, ${resizedUrl} ${targetWidth}w` : `${resizedUrl} ${targetWidth}w`;
	image.dataset.srcset = nextSrcset;
	image.dataset.src = resizedUrl;
}

function activateMenuImage(image, highPriority = true) {
	if (!image || image.dataset.menuImageRevealed === 'true') {
		return;
	}
	if (image.dataset.menuImageRevealing === 'true') {
		if (highPriority && 'fetchPriority' in image) {
			image.fetchPriority = 'high';
		}
		return;
	}

	ensureMenuImageResolution(image);
	image.dataset.menuImageRevealing = 'true';
	image.loading = 'eager';
	if ('fetchPriority' in image) {
		image.fetchPriority = highPriority ? 'high' : 'low';
	}

	const settleOnce = () => {
		if (hasLoadedMenuImageSource(image)) {
			settleMenuImage(image);
		}
	};
	const settleOnError = () => {
		settleMenuImage(image);
	};
	image.addEventListener('load', settleOnce, { once: true });
	image.addEventListener('error', settleOnError, { once: true });

	if (image.dataset.srcset) {
		image.setAttribute('srcset', image.dataset.srcset);
		image.removeAttribute('data-srcset');
	}

	if (image.dataset.sizes) {
		image.setAttribute('sizes', image.dataset.sizes);
		image.removeAttribute('data-sizes');
	}

	if (image.dataset.src) {
		image.src = image.dataset.src;
	}
	if (image.complete && hasLoadedMenuImageSource(image)) {
		settleMenuImage(image);
	}
}

function getPendingMenuImages(container) {
	if (!container) return;

	return Array.from(
		container.querySelectorAll('img.header-navigation-image[data-src], img.lazyload[data-src]')
	).filter(image => {
		return image.dataset.menuImageRevealed !== 'true' && image.dataset.menuImageRevealing !== 'true';
	});
}

function activateMenuImages(container, highPriority = true, limit = Infinity) {
	const images = getPendingMenuImages(container) || [];
	images.slice(0, limit).forEach(image => activateMenuImage(image, highPriority));
	return Array.from(container.querySelectorAll('img.header-navigation-image, img.lazyload'));
}

function primeMenuImages(container, limit = defaultMenuImageWarmupLimit) {
	if (!container || navigator.connection && navigator.connection.saveData) return;

	activateMenuImages(container, false, limit);
}

function waitForMenuImagesReady(container, timeout = 600, minReadyImages = 1) {
	if (!container) return Promise.resolve(true);

	const allImages = Array.from(container.querySelectorAll('img.header-navigation-image, img.lazyload'));
	if (!allImages.length) return Promise.resolve(true);

	const requiredReadyImages = Math.max(1, Math.min(minReadyImages, allImages.length));
	activateMenuImages(container, true, requiredReadyImages);
	const images = allImages.filter(image => {
		const currentSrc = image.currentSrc || image.getAttribute('src') || image.dataset.src || '';
		return currentSrc && currentSrc !== transparentPlaceholderSrc;
	});
	if (!images.length) return Promise.resolve(true);

	const initialReadyImages = images.filter(image => {
		return image.dataset.menuImageRevealed === 'true' || image.naturalWidth > 20;
	});
	if (initialReadyImages.length >= requiredReadyImages) return Promise.resolve(true);

	return new Promise(resolve => {
		let settled = false;
		let completedImages = 0;
		const readyImages = new Set(initialReadyImages);

		const finish = value => {
			if (settled) return;
			settled = true;
			resolve(value);
		};
		const markComplete = () => {
			completedImages += 1;
			if (completedImages >= images.length) {
				finish(readyImages.size > 0);
			}
		};
		const markReady = image => {
			readyImages.add(image);
			if (readyImages.size >= requiredReadyImages) {
				finish(true);
			}
		};

		images.forEach(image => {
			if (image.dataset.menuImageRevealed === 'true' || image.naturalWidth > 20) {
				markReady(image);
				markComplete();
				return;
			}

			image.addEventListener('load', () => {
				markReady(image);
				markComplete();
			}, { once: true });
			image.addEventListener('error', markComplete, { once: true });
		});

		if (!settled) {
			window.setTimeout(() => finish(false), timeout);
		}
	});
}

function getDesktopProductSections() {
	return Array.from(document.querySelectorAll(`[id^="${sectionNamePrefix}"]`));
}

function hasVisibleProductSection(excludedTarget) {
	return getDesktopProductSections().some(target => {
		if (target === excludedTarget) return false;

		const targetStyle = window.getComputedStyle(target);
		return targetStyle.display !== 'none' && Number(targetStyle.opacity) > 0.05;
	});
}

function revealLazyImages(container, attempt = 0) {
	if (!container) {
		return;
	}

	// 菜单图片初始不带 lazyload，只有用户悬停/聚焦/展开到可见面板后才触发网络加载。
	const candidateLazyImages = Array.from(
		container.querySelectorAll('img.header-navigation-image[data-src], img.lazyload[data-src]')
	);
	const unrevealedLazyImages = candidateLazyImages.filter(image => {
		if (image.dataset.menuImageRevealed === 'true') {
			return false;
		}

		if (image.dataset.menuImageRevealing === 'true') {
			return false;
		}

		if (image.classList.contains('lazyloaded') && !image.classList.contains('header-navigation-image--pending')) {
			image.dataset.menuImageRevealed = 'true';
			return false;
		}

		return true;
	});
	const visibleLazyImages = unrevealedLazyImages.filter(image => {
		return image.getBoundingClientRect().width > 0 && image.getBoundingClientRect().height > 0;
	});

	if (!visibleLazyImages.length) {
		if (unrevealedLazyImages.length) {
			window.requestAnimationFrame(() => {
				queueRevealRetry(container, attempt);
				if (window.lazySizes && window.lazySizes.loader) {
					window.lazySizes.loader.checkElems();
				}
			});
		}
		return;
	}

	clearRevealRetry(container);

	window.requestAnimationFrame(() => {
		visibleLazyImages.forEach(image => {
			activateMenuImage(image, true);
		});
	});
}

//  处理包含非 ASCII 字符的字符串 如
//  拉丁字母扩展（如 ä, ö, ü, é, ñ） 
//  希腊字母（如 α, β, γ）
//  俄文字母（如 А, Б, В）
//  中文字符（如 中, 国, 汉）
//  日文字符（如 日, 本, 語）
//  韩文字符（如 한, 글, 문）

function toBase64(str) {
	// 将字符串转换为 UTF-8 编码的 Uint8Array
	const encoder = new TextEncoder();
	const uint8Array = encoder.encode(str);
  
	// 将 Uint8Array 转换为 Base64 字符串
	return btoa(String.fromCharCode.apply(null, uint8Array));
  }
function setTitleActive(title) {
	// console.log(title)
	// console.log('btoa转bsae64后==',btoa(title))
	// console.log('toBase64转bsae64后==',toBase64(title))
	if (!title) return;
	const titleElement = document.getElementById(sectionTitlePrefix + toBase64(title));
	if (titleElement) {
		titleElement.classList.add('active');
	}
}

function setTitleInactive(title) {
	if (!title) return;
	const titleElement = document.getElementById(sectionTitlePrefix + toBase64(title));
	if (titleElement) {
		titleElement.classList.remove('active');
	}
}

function setActiveProductTitle(title) {
	if (!title) return;
	const targetId = sectionTitlePrefix + toBase64(title);
	document.querySelectorAll('.left .product-section-title').forEach(titleElement => {
		if (titleElement.id !== targetId) {
			titleElement.classList.remove('active');
			titleElement.setAttribute('aria-pressed', 'false');
		}
	});
	setTitleActive(title);
	const activeTitle = document.getElementById(targetId);
	if (activeTitle) activeTitle.setAttribute('aria-pressed', 'true');
}

function ensureDefaultProductSection() {
	if (!defaultCursor || defaultMenuPrepared) return;

	defaultMenuPrepared = true;
	setActiveProductTitle(titleCursor);
	showProductSection(titleCursor).then(shown => {
		if (!shown) defaultMenuPrepared = false;
	});
}

function isDesktopMenuViewport() {
	return !window.matchMedia || window.matchMedia('(min-width: 1068px)').matches;
}

function isMobileMenuViewport() {
	return !isDesktopMenuViewport();
}

function getProductSectionByTitle(title) {
	if (!title) return null;
	return document.getElementById(sectionNamePrefix + toBase64(title));
}

function warmupDefaultProductSection() {
	if (!defaultCursor || defaultMenuWarmupStarted || !isDesktopMenuViewport()) return;

	const target = getProductSectionByTitle(defaultCursor);
	if (!target) return;

	defaultMenuWarmupStarted = true;
	warmupProductSection(target).then(loaded => {
		if (!loaded) {
			defaultMenuWarmupStarted = false;
			return;
		}

		primeMenuImages(target);
	});
}

function warmupProductSection(target) {
	if (!target || !isDesktopMenuViewport()) {
		return Promise.resolve(false);
	}
	if (!target.dataset.menuPanelUrl) {
		primeMenuImages(target);
		return Promise.resolve(true);
	}

	return loadDeferredProductPanel(target, 'desktop', { reveal: false });
}

function warmupDesktopProductSections() {
	if (desktopMenuImageWarmupStarted || !isDesktopMenuViewport()) return;
	if (navigator.connection && navigator.connection.saveData) return;

	desktopMenuImageWarmupStarted = true;
	getDesktopProductSections().forEach(target => {
		primeMenuImages(target, 1);
	});
}

function scheduleDefaultProductWarmup() {
	if (!defaultCursor || !isDesktopMenuViewport()) return;

	const scheduleIdleDefaultWarmup = () => {
		if ('requestIdleCallback' in window) {
			window.requestIdleCallback(warmupDefaultProductSection, { timeout: 1800 });
			return;
		}

		window.setTimeout(warmupDefaultProductSection, 1000);
	};

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', scheduleIdleDefaultWarmup, { once: true });
	} else {
		scheduleIdleDefaultWarmup();
	}
}

function setTitleCursor(newCursor) {
	if (newCursor === titleCursor) return;
	const oldCursor = titleCursor;
	titleCursor = newCursor;
	cursorChangeHook(oldCursor, newCursor);
}

function cursorChangeHook(oldCursor, newCursor) {
	if (newCursor === oldCursor) return;
	setActiveProductTitle(newCursor);
	showProductSection(newCursor);
}

async function sleep(ms) {
	return new Promise(r => {
		setTimeout(() => {
			r();
		}, ms);
	});
}

function showProductSection(title) {
	if (!title) return;

	const target = getProductSectionByTitle(title);
	if (!target) return;

	++productSectionSwitchToken;
	target.dataset.menuPanelActive = 'true';
	clearProductSectionHideTimer(target);
	hideProductSectionsExcept(title, true);
	target.style.display = 'flex';
	target.style.opacity = 1;
	revealLazyImages(target);

	if (target.dataset.menuPanelUrl && target.querySelector('.header-menu-deferred-placeholder')) {
		loadDeferredProductPanel(target, 'desktop', { reveal: false }).then(loaded => {
			if (!loaded || target.dataset.menuPanelActive !== 'true') return;
			revealLazyImages(target);
		});
	}

	return Promise.resolve(true);
}

async function hideProductSection(title) {
	if (!title) return;

	let target = getProductSectionByTitle(title);
	if (!target) return;

	hideProductSectionElement(target);
}

function hideProductSectionElement(target, immediate = false) {
	if (!target) return;

	target.dataset.menuPanelActive = 'false';
	target.style.opacity = 0;
	clearProductSectionHideTimer(target);

	if (immediate) {
		target.style.display = 'none';
		productSectionHideTimers.delete(target);
		return;
	}

	const hideTimer = window.setTimeout(() => {
		if (target.dataset.menuPanelActive !== 'true') {
			target.style.display = 'none';
		}
		productSectionHideTimers.delete(target);
	}, 200);
	productSectionHideTimers.set(target, hideTimer);
}

function hideProductSectionsExcept(activeTitle, immediate = false) {
	const activeTarget = getProductSectionByTitle(activeTitle);
	getDesktopProductSections().forEach(target => {
		if (target !== activeTarget) {
			hideProductSectionElement(target, immediate);
		}
	});
}

document.querySelectorAll('.left .product-section-title:not(.menu-btn)').forEach(el => {
	['mouseenter', 'focusin'].forEach(eventName => {
		el.addEventListener(eventName, () => {
			setTitleCursor(el.innerText);
		});
	});
});

function activateDesktopMegaMenu() {
	warmupDefaultProductSection();
	warmupDesktopProductSections();
	ensureDefaultProductSection();
}

const desktopMegaMenuItems = Array.from(document.querySelectorAll('.menu-item-has-megamenu'));
desktopMegaMenuItems.forEach(el => {
	el.addEventListener('pointerenter', () => {
		warmupDefaultProductSection();
		warmupDesktopProductSections();
	}, { passive: true });

	el.addEventListener('mouseenter', activateDesktopMegaMenu);

	el.addEventListener('focusin', activateDesktopMegaMenu);
});

const activeDesktopMegaMenu = desktopMegaMenuItems.find(el => el.matches(':hover') || el.matches(':focus-within'));
if (activeDesktopMegaMenu) {
	activateDesktopMegaMenu();
}

bindProductItemLinks(document);
scheduleDefaultProductWarmup();


const megaMenuContainer = document.querySelector('.sub-menu.mega-menu-container');
if (megaMenuContainer) {
	megaMenuContainer.addEventListener('mouseleave', e => {
		setTitleCursor(defaultCursor);
	});
}

/* Mobile Part */

function getMobileDeferredProductPanels() {
	return Array.from(document.querySelectorAll('.full-menu-mobile-deferred[data-menu-panel-url]'));
}

function warmupMobileProductPanels(options = {}) {
	if (!isMobileMenuViewport()) return;

	const includeImages = options.includeImages === true;
	const limit = options.limit || 0;
	const targets = getMobileDeferredProductPanels();
	if (!targets.length) return;
	const warmupTargets = limit > 0 ? targets.slice(0, limit) : targets;

	const warmupTarget = (target, index) => {
		loadDeferredProductPanel(target, 'mobile', { reveal: false }).then(loaded => {
			if (!loaded) return;

			if (includeImages && index === 0) {
				activateMenuImages(target, true);
			}

			if (target.closest('.full-menu-mobile-details[open]')) {
				revealLazyImages(target);
			}
		});
	};

	warmupTargets.forEach((target, index) => {
		if (!mobileMenuPanelWarmupTargets.has(target)) {
			mobileMenuPanelWarmupTargets.add(target);
			window.setTimeout(() => {
				warmupTarget(target, index);
			}, index * 100);
		} else if (includeImages && index === 0 && target.dataset.menuPanelLoaded === 'mobile') {
			activateMenuImages(target, true);
			if (target.closest('.full-menu-mobile-details[open]')) {
				revealLazyImages(target);
			}
		}
	});

	if (includeImages && !mobileMenuImageWarmupStarted) {
		mobileMenuImageWarmupStarted = true;
		const firstTarget = targets[0];
		if (firstTarget) {
			warmupTarget(firstTarget, 0);
		}
	}
}

const mobileToggleButton = document.querySelector('.mobile-toggle');
if (mobileToggleButton) {
	['pointerdown', 'mousedown', 'touchstart', 'focus'].forEach(eventName => {
		mobileToggleButton.addEventListener(eventName, () => {
			warmupMobileProductPanels({ limit: 2 });
		}, { passive: eventName !== 'focus' });
	});
}

function bindMobileMenuLink(details) {
	if (mobileMenuLinkBindings.has(details)) return;
	if (!details.querySelector('.full-menu-mobile-deferred[data-menu-panel-url]')) return;
	mobileMenuLinkBindings.add(details);

	const summary = details.querySelector('summary.parent-link');
	if (summary) {
		['pointerdown', 'mousedown', 'touchstart', 'focus', 'click'].forEach(eventName => {
			summary.addEventListener(eventName, () => {
				warmupMobileProductPanels({ includeImages: true, limit: 2 });
				window.setTimeout(() => {
					warmupMobileProductPanels();
				}, 120);
			}, { passive: eventName !== 'focus' });
		});
	}

	details.addEventListener('toggle', () => {
		if (details.open) {
			warmupMobileProductPanels({ includeImages: true, limit: 2 });
			window.setTimeout(() => {
				warmupMobileProductPanels();
			}, 120);
		}
	});
}

function getMobileMenuDrawer(element) {
	return element && element.closest('#mobile-menu') || document.querySelector('#mobile-menu');
}

function getMobileDetailsSummary(details) {
	if (!details) return null;
	return details.querySelector(':scope > summary') || details.querySelector('summary');
}

function getMobileDeferredPanel(details) {
	return details ? details.querySelector('.full-menu-mobile-deferred[data-menu-panel-url]') : null;
}

function keepMobileSummaryAnchored(details, previousTop) {
	const drawer = getMobileMenuDrawer(details);
	const summary = getMobileDetailsSummary(details);
	if (!drawer || !summary) return;

	const drawerRect = drawer.getBoundingClientRect();
	const summaryRect = summary.getBoundingClientRect();
	const minTop = drawerRect.top + mobileMenuAnchorTopGap;
	const maxTop = drawerRect.top + Math.min(drawer.clientHeight * 0.42, 240);
	let targetTop = Number.isFinite(previousTop) ? previousTop : summaryRect.top;

	targetTop = Math.max(minTop, Math.min(targetTop, maxTop));
	const delta = summaryRect.top - targetTop;
	if (Math.abs(delta) > 1) {
		drawer.scrollTop += delta;
	}
}

function scheduleMobileSummaryAnchor(details, previousTop) {
	window.requestAnimationFrame(() => keepMobileSummaryAnchored(details, previousTop));
	window.setTimeout(() => keepMobileSummaryAnchored(details, previousTop), 80);
	window.setTimeout(() => keepMobileSummaryAnchored(details, previousTop), 240);
	window.setTimeout(() => keepMobileSummaryAnchored(details, previousTop), 640);
}

function closeOtherMobileProductDetails(activeDetails) {
	document.querySelectorAll('.full-menu-mobile-details[open]').forEach(details => {
		if (details !== activeDetails) {
			details.open = false;
		}
	});
}

function prepareMobileProductDetails(details, options = {}) {
	if (!details || !isMobileMenuViewport()) {
		return Promise.resolve(false);
	}

	const deferredPanel = getMobileDeferredPanel(details);
	if (!deferredPanel) {
		if (options.revealImages !== false) {
			revealLazyImages(details);
		}
		return Promise.resolve(true);
	}

	return loadDeferredProductPanel(deferredPanel, 'mobile', { reveal: false }).then(loaded => {
		if ((loaded || deferredPanel.dataset.menuPanelLoaded === 'mobile') && options.revealImages !== false) {
			revealLazyImages(details);
		}
		return loaded;
	});
}

function bindMobileProductDetails(el) {
	if (mobileProductDetailsBindings.has(el)) return;
	mobileProductDetailsBindings.add(el);

	const summary = getMobileDetailsSummary(el);
	if (summary) {
		['pointerdown', 'touchstart', 'focus'].forEach(eventName => {
			summary.addEventListener(eventName, () => {
				prepareMobileProductDetails(el, { revealImages: false });
			}, { passive: eventName !== 'focus' });
		});

		summary.addEventListener('click', event => {
			if (!isMobileMenuViewport()) return;

			event.preventDefault();
			const wasOpen = el.open;
			const previousTop = summary.getBoundingClientRect().top;

			if (wasOpen) {
				el.open = false;
				return;
			}

			closeOtherMobileProductDetails(el);
			el.open = true;
			scheduleMobileSummaryAnchor(el, previousTop);
			prepareMobileProductDetails(el).then(() => {
				revealLazyImages(el);
				scheduleMobileSummaryAnchor(el, previousTop);
			});
		});
	}

	el.addEventListener('toggle', () => {
		if (el.open) {
			closeOtherMobileProductDetails(el);
			const summaryElement = getMobileDetailsSummary(el);
			const previousTop = summaryElement ? summaryElement.getBoundingClientRect().top : null;
			scheduleMobileSummaryAnchor(el, previousTop);
			prepareMobileProductDetails(el).then(() => {
				revealLazyImages(el);
				scheduleMobileSummaryAnchor(el, previousTop);
			});
		}
	});
}

function initializeMobileMenuInteractions(root = document) {
	root.querySelectorAll('.mobile-menu .link-container').forEach(bindMobileMenuLink);
	root.querySelectorAll('.full-menu-mobile-details').forEach(bindMobileProductDetails);
}

initializeMobileMenuInteractions();

document.addEventListener('pc:mobile-menu:hydrated', event => {
	initializeMobileMenuInteractions(event.detail && event.detail.menu ? event.detail.menu : document);
});
